import { useState, useEffect } from "react"
import { X, DollarSign, Save, Trash2 } from "lucide-react"

/**
 * EditItemModal — edit a collection item or wishlist item in-place.
 *
 * mode = "collection" → quantity, condition, purchase_price, notes
 * mode = "wishlist"   → priority, notes
 */
export default function EditItemModal({
	isOpen,
	onClose,
	onSave,
	onDelete,
	item,
	mode = "collection", // "collection" | "wishlist"
}) {
	const [quantity, setQuantity] = useState(1)
	const [condition, setCondition] = useState("Near Mint")
	const [purchasePrice, setPurchasePrice] = useState("")
	const [targetPrice, setTargetPrice] = useState("")
	const [notes, setNotes] = useState("")
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (isOpen && item) {
			if (mode === "collection") {
				setQuantity(item.quantity || 1)
				setCondition(item.condition || "Near Mint")
				setPurchasePrice(item.purchase_price != null ? item.purchase_price.toString() : "")
				setNotes(item.notes || "")
			} else {
				setTargetPrice(item.target_price != null ? item.target_price.toString() : "")
				setNotes(item.notes || "")
			}
		}
	}, [isOpen, item, mode])

	const handleSave = async () => {
		setSaving(true)
		try {
			if (mode === "collection") {
				await onSave({
					quantity: parseInt(quantity) || 1,
					condition,
					purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
					notes: notes.trim() || null,
				})
			} else {
				await onSave({
					target_price: targetPrice ? parseFloat(targetPrice) : null,
					notes: notes.trim() || null,
				})
			}
			onClose()
		} catch (err) {
			console.error("Error saving item:", err)
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		if (!window.confirm("Remove this card?")) return
		setSaving(true)
		try {
			await onDelete()
			onClose()
		} catch (err) {
			console.error("Error deleting item:", err)
		} finally {
			setSaving(false)
		}
	}

	if (!isOpen || !item) return null

	const cardName = item.card_data?.name || item.card_id
	const cardImage = item.card_data?.image_small || null

	return (
		<div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
			onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
			<div className="bg-[#1a1a2e] border border-purple/30 rounded-xl max-w-lg w-full shadow-2xl shadow-purple/10">
				{/* Header */}
				<div className="flex items-center justify-between p-5 border-b border-purple/20">
					<h2 className="text-lg font-semibold text-cream">
						{mode === "collection" ? "Edit Collection Item" : "Edit Wishlist Item"}
					</h2>
					<button onClick={onClose}
						className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Card Preview */}
				<div className="flex items-center gap-4 px-5 pt-4">
					{cardImage ? (
						<img src={cardImage} alt={cardName}
							className="w-16 h-[89px] object-cover rounded-md border border-purple/20" />
					) : (
						<div className="w-16 h-[89px] rounded-md border border-purple/20 bg-dark flex items-center justify-center">
							<span className="text-cream/40 text-[10px] text-center">{item.card_id}</span>
						</div>
					)}
					<div>
						<h3 className="text-cream font-medium">{cardName}</h3>
						<p className="text-cream/50 text-sm">{item.card_id}</p>
						{item.card_data?.rarity && (
							<span className="text-xs text-purple/80">{item.card_data.rarity}</span>
						)}
					</div>
				</div>

				{/* Fields */}
				<div className="p-5 space-y-4">
					{mode === "collection" ? (
						<>
							{/* Quantity */}
							<div>
								<label className="block text-sm font-medium text-cream/70 mb-1.5">Quantity</label>
								<input type="number" min="1" value={quantity}
									onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
									className="w-full px-3 py-2 bg-dark/80 border border-purple/20 rounded-lg text-cream
										focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50" />
							</div>

							{/* Condition */}
							<div>
								<label className="block text-sm font-medium text-cream/70 mb-1.5">Condition</label>
								<select value={condition}
									onChange={(e) => setCondition(e.target.value)}
									className="w-full px-3 py-2 bg-dark/80 border border-purple/20 rounded-lg text-cream
										focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50">
									<option value="Mint">Mint</option>
									<option value="Near Mint">Near Mint</option>
									<option value="Lightly Played">Lightly Played</option>
									<option value="Moderately Played">Moderately Played</option>
									<option value="Heavily Played">Heavily Played</option>
									<option value="Damaged">Damaged</option>
								</select>
							</div>

							{/* Purchase Price */}
							<div>
								<label className="block text-sm font-medium text-cream/70 mb-1.5">Purchase Price</label>
								<div className="relative">
									<DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
									<input type="number" step="0.01" min="0"
										placeholder="0.00" value={purchasePrice}
										onChange={(e) => setPurchasePrice(e.target.value)}
										className="w-full pl-9 pr-4 py-2 bg-dark/80 border border-purple/20 rounded-lg text-cream
											placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50" />
								</div>
							</div>
						</>
					) : (
						/* Wishlist: Target Price */
						<div>
							<label className="block text-sm font-medium text-cream/70 mb-1.5">Target Price</label>
							<p className="text-xs text-cream/40 mb-2">Set a target buy price to track against market value.</p>
							<div className="relative">
								<DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
								<input type="number" step="0.01" min="0"
									placeholder="e.g. 5.00" value={targetPrice}
									onChange={(e) => setTargetPrice(e.target.value)}
									className="w-full pl-9 pr-4 py-2 bg-dark/80 border border-purple/20 rounded-lg text-cream
										placeholder-cream/30 focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50" />
							</div>
						</div>
					)}

					{/* Notes (both modes) */}
					<div>
						<label className="block text-sm font-medium text-cream/70 mb-1.5">Notes</label>
						<textarea rows={2} value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder="Add a note..."
							className="w-full px-3 py-2 bg-dark/80 border border-purple/20 rounded-lg text-cream
								placeholder-cream/30 resize-none focus:outline-none focus:ring-2 focus:ring-purple/50 focus:border-purple/50" />
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-between p-5 border-t border-purple/20">
					<button onClick={handleDelete} disabled={saving}
						className="flex items-center gap-1.5 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10
							rounded-lg text-sm transition-colors disabled:opacity-50">
						<Trash2 className="w-4 h-4" />
						Remove
					</button>
					<div className="flex gap-2">
						<button onClick={onClose}
							className="px-4 py-2 text-cream/70 hover:text-cream hover:bg-white/5 rounded-lg text-sm transition-colors">
							Cancel
						</button>
						<button onClick={handleSave} disabled={saving}
							className="flex items-center gap-1.5 px-5 py-2 bg-purple hover:bg-purple/90 text-white rounded-lg
								text-sm font-medium transition-colors disabled:opacity-50">
							{saving ? (
								<div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
							) : (
								<Save className="w-4 h-4" />
							)}
							Save
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}
